import { ReactElement, Fragment } from "react";

import { decode } from "he";

import * as ADF from "../../adf";
import { Anchor } from "../anchor";
import { useRenderer } from "../renderer";

export type RichTextProps = {
    readonly className?: string;
    readonly document: ADF.Document;
};

export const RichText = ({
    className,
    document
}: RichTextProps): ReactElement => {
    // Safety check for undefined document or content
    if (!document || !document.content) {
        return <div className={className} />;
    }

    return (
        <div className={className}>
            {Array.from(document.content)
                .map(mapNodeToReactElement)
                .reduce((acc, val) => acc.concat(val), [])}
        </div>
    );
};

function mapNodeToReactElement(
    node: ADF.Node,
    index?: number
): readonly ReactElement[] {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- Called within React context
    const renderer = useRenderer();
    switch (node.type) {
        case "blockquote":
            return [mapBlockquoteToReactElement(node, index)];
        case "bulletList":
            return [
                <ul key={`ul-${index}`}>
                    {node.content.map(mapNodeToReactElement)}
                </ul>
            ];
        case "heading":
            return [mapHeadingToReactElement(node, index)];
        case "listItem":
            return [
                <li key={`li-${index}`}>
                    {node.content.map(mapNodeToReactElement)}
                </li>
            ];
        case "paragraph":
            return [
                <p key={`p-${index}`}>
                    {node.content?.map(mapNodeToReactElement)}
                </p>
            ];
        case "rule":
            return [<hr role="presentation" key={`hr-${index}`} />];
        case "text":
            return mapTextToReactElement(node, index);
        case "orderedList":
            return [
                <ol key={`ol-${index}`}>
                    {node.content.map(mapNodeToReactElement)}
                </ol>
            ];
        case "bxpEmbeddedViewBlock":
            return [
                <Fragment key={`block-${index}`}>
                    {renderer(node.model)}
                </Fragment>
            ];
        case "bxpEmbeddedViewInline":
            return [
                <Fragment key={`inline-${index}`}>
                    {renderer(node.model)}
                </Fragment>
            ];
    }
}

function mapBlockquoteToReactElement(
    { content }: ADF.Blockquote,
    index?: number
): ReactElement {
    return (
        <blockquote key={`blockquote-${index}`}>
            {content.map(mapNodeToReactElement)}
        </blockquote>
    );
}

function mapHeadingToReactElement(
    { attrs, content = [] }: ADF.Heading,
    index?: number
): ReactElement {
    const children = content.map(mapNodeToReactElement);
    switch (attrs.level) {
        case 1:
            return <h1 key={`h1-${index}`}>{children}</h1>;
        case 2:
            return <h2 key={`h2-${index}`}>{children}</h2>;
        case 3:
            return <h3 key={`h3-${index}`}>{children}</h3>;
        case 4:
            return <h4 key={`h4-${index}`}>{children}</h4>;
        case 5:
            return <h5 key={`h5-${index}`}>{children}</h5>;
        case 6:
            return <h6 key={`h6-${index}`}>{children}</h6>;
    }
}

function mapTextToReactElement(
    { text, marks = [] }: ADF.Text,
    index?: number
): readonly ReactElement[] {
    const [mark, ...otherMarks] = marks;
    if (mark) {
        const children = mapTextToReactElement(
            {
                type: "text",
                text,
                marks: otherMarks
            },
            index
        );
        switch (mark.type) {
            case "code":
                return [<code key={`code-${index}`}>{children}</code>];
            case "em":
                return [<em key={`em-${index}`}>{children}</em>];
            case "strong":
                return [<strong key={`strong-${index}`}>{children}</strong>];
            case "underline":
                return [<u key={`u-${index}`}>{children}</u>];
            case "link":
                return [
                    <Anchor key={`a-${index}`} href={mark.attrs.href}>
                        {children}
                    </Anchor>
                ];
        }
    } else {
        const [firstChunk, ...otherChunks] = text.split("\n");
        if (otherChunks.length > 0) {
            const otherElements = otherChunks
                .map((otherChunk: string, _index: number) => [
                    <br key={`br-${_index}`} />,
                    <Fragment key={`other-fragment-${_index}`}>
                        {decode(otherChunk || "")}
                    </Fragment>
                ])
                .reduce((acc, val) => acc.concat(val), []);
            return [
                <Fragment key={`first-fragment-${index}`}>
                    {decode(firstChunk || "")}
                </Fragment>,
                ...otherElements
            ];
        } else {
            return [
                <Fragment key={`fragment-${index}`}>{decode(text)}</Fragment>
            ];
        }
    }
}
